import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Fragment } from "react";

import Button from "../Button";
import Header from "../Header";
import { Icon } from "../Icon";

interface DrawerProps {
  avatar?: React.ReactNode;
  children: React.ReactNode;
  description: string;
  disableBackdrop?: boolean;
  disableOutsideClick?: boolean;
  footer?: React.ReactNode;
  icon?: React.ComponentProps<typeof Icon>["name"];
  isOpen: boolean;
  leftContent?: React.ReactNode;
  onClose: () => void;
  title: string;
}

export default function Drawer({
  isOpen,
  onClose,
  title,
  description,
  icon,
  avatar,
  children,
  footer,
  disableBackdrop = false,
  disableOutsideClick = false,
  leftContent,
}: DrawerProps) {
  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={disableOutsideClick ? () => {} : onClose}
      >
        {!disableBackdrop && (
          <TransitionChild
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>
        )}

        {leftContent}

        <div
          className={`fixed inset-0 overflow-hidden ${disableBackdrop && disableOutsideClick ? "pointer-events-none" : ""}`}
        >
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <TransitionChild
                as={Fragment}
                enter="transform transition ease-in-out duration-500"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <DialogPanel className="pointer-events-auto w-screen max-w-2xl">
                  <div className="flex h-full flex-col overflow-hidden bg-white shadow-xl">
                    <div className="sticky top-0 z-10 bg-white">
                      <Header
                        title={title}
                        icon={icon}
                        avatar={avatar}
                        description={description}
                        rightActions={
                          <Button
                            icon="xMark"
                            className="!p-0 hover:!bg-white"
                            iconClassName="h-6"
                            onClick={onClose}
                            plain
                          />
                        }
                      />
                    </div>
                    <div className="relative flex-1 overflow-y-auto px-4">
                      <div className="mx-auto max-w-2xl">{children}</div>
                    </div>
                    {footer && (
                      <div className="sticky bottom-0 z-10 bg-white">
                        {footer}
                      </div>
                    )}
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
